import { DoctoralProfileExtension } from '@/features/doctoral/components/DoctoralProfileExtension';
import { useMemo, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { DRAWER_ROLE_SWITCH } from '@/lib/navigation';
import { routes } from '@/lib/routes';
import { PROFILE_SECTIONS, type ProfileSectionId } from '../data';
import { useCanonicalProfile } from '../useCanonicalProfile';
import { ProfileSectionBody } from './ProfileSectionBody';

type ProfileHubProps = {
  sectionId: ProfileSectionId;
};

function roleBadge(code: string): string {
  return DRAWER_ROLE_SWITCH.find((item) => item.code === code)?.label ?? code;
}

function displayFullName(
  profile: ReturnType<typeof useCanonicalProfile>['profile'],
  fallback: string,
): string {
  const ru = profile?.personal?.ru;
  if (ru?.given || ru?.family) {
    return [ru.given, ru.middle, ru.family].filter(Boolean).join(' ');
  }
  return fallback;
}

function profileInitials(fullName: string, fallback = 'СЖ'): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}` || fallback;
  }
  return fallback;
}

function saveLabel(state: string): string {
  if (state === 'saving') return 'Сохраняется';
  if (state === 'dirty') return 'Есть изменения';
  if (state === 'error') return 'Ошибка';
  return 'Сохранено';
}

export const ProfileHub = ({ sectionId }: ProfileHubProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, health, loading, saveState, setField, setSection, saveNow, sectionPct } =
    useCanonicalProfile();

  const sections = useMemo(
    () =>
      PROFILE_SECTIONS.map((section) => ({
        ...section,
        pct: sectionPct(section.id),
      })),
    [sectionPct],
  );

  const active = sections.find((section) => section.id === sectionId) ?? sections[0];
  const score = health?.score ?? 0;
  const filled = health?.filled_sections ?? 0;
  const total = health?.total_sections ?? 11;
  const name = displayFullName(profile, user?.fullName || 'Пользователь');
  const initials = profileInitials(name, user?.initials || 'СЖ');
  const academiaId = String(profile?.account?.academiaId || user?.academiaId || 'ZA000001');
  const email = String(profile?.account?.email || user?.email || '');
  const nextSteps = health?.next_steps?.length
    ? health.next_steps
    : [{ title: 'Профиль', text: 'Заполните разделы для расчёта health', accent: '#41d2ff' }];

  const handleClose = () => navigate(routes.app);
  const handleBack = () => navigate(-1);

  return (
    <div className="profile-overlay" role="dialog" aria-modal="true" aria-label="Мой профиль">
      <div className="profile-shell">
        <header className="profile-top">
          <button type="button" className="profile-btn" onClick={handleBack}>
            ← Назад
          </button>
          <div className="profile-top-title">
            <h1>Мой профиль · {active.title}</h1>
          </div>
          <div className="profile-top-actions">
            <span
              className={`profile-autosave${saveState === 'dirty' ? ' dirty' : ''}${saveState === 'saving' ? ' saving' : ''}${saveState === 'error' ? ' error' : ''}`}
            >
              {saveLabel(saveState)}
            </span>
            <button
              type="button"
              className="profile-btn primary"
              onClick={() => void saveNow()}
              disabled={loading}
            >
              Сохранить
            </button>
            <button
              type="button"
              className="profile-btn primary"
              onClick={() => navigate(routes.profileExport('cv'))}
            >
              CV / резюме
            </button>
            <button
              type="button"
              className="profile-btn primary"
              onClick={() => navigate(routes.profileExport('works'))}
            >
              Научные труды
            </button>
            <button
              type="button"
              className="profile-btn notify"
              onClick={() =>
                navigate(routes.notifications, {
                  state: { returnTo: routes.profileSection(sectionId) },
                })
              }
            >
              Уведомления
            </button>
            <span className="profile-top-av" aria-hidden="true">
              {initials}
            </span>
            <button type="button" className="profile-btn" onClick={handleClose}>
              Закрыть ×
            </button>
          </div>
        </header>

        <div className="profile-body">
          <section className="profile-hero">
            <div className="profile-hero-av" aria-hidden="true">
              {profile?.personal?.avatarUrl ? (
                <img src={profile.personal.avatarUrl} alt="" />
              ) : user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="" />
              ) : (
                initials
              )}
            </div>
            <div className="profile-hero-person">
              <small>Мой профиль</small>
              <h2>{name}</h2>
              <p>
                {academiaId} · {email}
              </p>
              <div className="profile-badges">
                <span className="is-good">Активен</span>
                <span>{roleBadge(user?.activeRole || 'SYSTEM_OWNER')}</span>
                <span>ID: {academiaId}</span>
                <span className={saveState === 'saved' ? 'is-saved' : ''}>
                  {saveLabel(saveState)}
                </span>
              </div>
            </div>
            <div className="profile-hero-kpi">
              <article className="profile-kpi" style={{ '--a': '#49ddb7' } as CSSProperties}>
                <small>Заполненность</small>
                <b>{score}%</b>
                <i className="profile-kpi-bar">
                  <i style={{ width: `${score}%` }} />
                </i>
              </article>
              <article className="profile-kpi" style={{ '--a': '#41d2ff' } as CSSProperties}>
                <small>Разделы ≥70%</small>
                <b>
                  {filled}/{total}
                </b>
                <span>готовые блоки</span>
              </article>
              <article className="profile-kpi" style={{ '--a': '#9e80ff' } as CSSProperties}>
                <small>Публикации</small>
                <b>{health?.publication_count ?? 0}</b>
                <span>в реестре</span>
              </article>
              <article className="profile-kpi" style={{ '--a': '#e2ad3a' } as CSSProperties}>
                <small>Проекты / гранты</small>
                <b>{health?.project_count ?? 0}</b>
                <span>активных</span>
              </article>
            </div>
          </section>

          <div className="profile-layout">
            <aside className="profile-nav" aria-label="Разделы профиля">
              <div className="profile-nav-head">
                <small>11 разделов</small>
                <strong>Мой профиль</strong>
                <span>Заполняйте разделы по порядку — прогресс обновляется по сохранённым сведениям</span>
              </div>
              {sections.map((section) => (
                <Link
                  key={section.id}
                  to={routes.profileSection(section.id)}
                  className={`profile-tab${section.id === active.id ? ' is-active' : ''}`}
                  style={{ '--a': section.accent, '--pct': `${section.pct}%` } as CSSProperties}
                >
                  <span
                    className="profile-tab-icon"
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: section.icon }}
                  />
                  <span className="profile-tab-copy">
                    <small>{section.num}</small>
                    <strong>{section.title}</strong>
                    <span>{section.text}</span>
                  </span>
                  <span className="profile-tab-pct">{section.pct}%</span>
                  <span className="profile-tab-bar" aria-hidden="true">
                    <i style={{ width: `${section.pct}%` }} />
                  </span>
                </Link>
              ))}
            </aside>

            <main className="profile-main">
              <header style={{ '--a': active.accent } as CSSProperties}>
                <span
                  className="profile-main-icon"
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{ __html: active.icon }}
                />
                <div>
                  <h2>{active.title}</h2>
                  <p>{active.text}</p>
                </div>
                <div className="profile-main-actions">
                  <span className="profile-tab-pct">{active.pct}%</span>
                  <button
                    type="button"
                    className="profile-btn primary"
                    onClick={() => void saveNow()}
                  >
                    Сохранить
                  </button>
                </div>
              </header>
              <div className="profile-main-body">
                {loading || !profile ? (
                  <div className="profile-placeholder">
                    <h3>Загрузка профиля…</h3>
                    <p>Canonical profile store</p>
                  </div>
                ) : (
                  <>
                    <ProfileSectionBody
                      sectionId={active.id}
                      profile={profile}
                      saveState={saveState}
                      setField={setField}
                      setSection={setSection}
                      saveNow={saveNow}
                    />
                    {user?.categories.includes('DOCTORAL_STUDENT') && (
                      <DoctoralProfileExtension
                        section={active.id}
                        profile={profile}
                        setField={setField}
                        setSection={setSection}
                        saveNow={saveNow}
                      />
                    )}
                  </>
                )}
              </div>
            </main>

            <aside className="profile-side" aria-label="Состояние профиля">
              <section className="profile-side-card">
                <small>Profile Health</small>
                <h3>Заполненность</h3>
                <div className="profile-ring-row">
                  <div className="profile-ring" style={{ '--v': score } as CSSProperties}>
                    <b>{score}%</b>
                  </div>
                  <div>
                    <strong>
                      {filled} из {total} разделов
                    </strong>
                    <span>Учитываются только заполненные сведения профиля</span>
                  </div>
                </div>
              </section>

              <section className="profile-side-card">
                <small>Next steps</small>
                <h3>Следующие шаги</h3>
                <div className="profile-next">
                  {nextSteps.map((step) => (
                    <article
                      key={step.title}
                      style={{ '--a': step.accent || '#41d2ff' } as CSSProperties}
                    >
                      <strong>{step.title}</strong>
                      <small>{step.text}</small>
                    </article>
                  ))}
                </div>
              </section>

              <section className="profile-side-card">
                <small>Academic Record</small>
                <h3>Кратко</h3>
                <div className="profile-mini">
                  <div>
                    <b>{(profile?.achievements || []).length}</b>
                    <small>достижений</small>
                  </div>
                  <div>
                    <b>{(profile?.documents || []).length}</b>
                    <small>документов</small>
                  </div>
                  <div>
                    <b>{health?.publication_count ?? 0}</b>
                    <small>публикаций</small>
                  </div>
                  <div>
                    <b>{(profile?.education || []).filter((x) => !x.archived).length}</b>
                    <small>образование</small>
                  </div>
                </div>
              </section>
            </aside>
          </div>

          <footer className="profile-bottom">
            <button type="button" className="profile-btn primary" onClick={() => void saveNow()}>
              Сохранить изменения
            </button>
            <button
              type="button"
              className="profile-btn"
              onClick={() => navigate(routes.profileSection('settings'))}
            >
              Публичный профиль
            </button>
            <button
              type="button"
              className="profile-btn"
              onClick={() => navigate(routes.profileExport('cv'))}
            >
              Скачать CV / резюме
            </button>
            <button
              type="button"
              className="profile-btn"
              onClick={() => navigate(routes.profileExport('works'))}
            >
              Научные труды
            </button>
            <span className="profile-bottom-spacer" />
            <button type="button" className="profile-btn" onClick={handleClose}>
              ← Назад в кабинет
            </button>
            <button type="button" className="profile-btn" onClick={handleClose}>
              Закрыть
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};
